import { useCustom, useCustomMutation } from '@refinedev/core';
import { Table, Tag, Button, Modal, message } from 'antd';
import { Clock, RefreshCw, Pause } from 'lucide-react';
import type { FC } from 'react';

interface GracePeriodRecord {
  id: string;
  organizationId: string;
  organizationName: string;
  planType: string;
  gracePeriodStart: string;
  gracePeriodEnd: string;
  daysRemaining: number;
  status: string;
}

const fmtDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString('fr-FR') : '--';

const GracePeriodPanel: FC = () => {
  const { data, isLoading, refetch } = useCustom<{
    data: { items: GracePeriodRecord[] };
  }>({
    url: '/api/v1/admin/billing/subscriptions',
    method: 'get',
    config: { query: { status: 'grace_period' } },
  });

  const { mutate: reactivate, isLoading: reactivating } = useCustomMutation();
  const { mutate: suspend, isLoading: suspending } = useCustomMutation();

  const records = data?.data?.data?.items ?? [];

  const handleReactivate = (r: GracePeriodRecord) => {
    Modal.confirm({
      title: 'Reactiver cet abonnement ?',
      content: `L'organisation "${r.organizationName || r.organizationId.slice(0, 8)}" sera reactivee.`,
      okText: 'Reactiver',
      cancelText: 'Annuler',
      onOk: () =>
        new Promise<void>((resolve) => {
          reactivate(
            {
              url: `/api/v1/admin/billing/subscriptions/${r.organizationId}/reactivate`,
              method: 'post',
              values: {},
            },
            {
              onSuccess: () => {
                void message.success('Abonnement reactive');
                void refetch();
                resolve();
              },
              onError: () => {
                void message.error('Erreur lors de la reactivation');
                resolve();
              },
            },
          );
        }),
    });
  };

  const handleSuspend = (r: GracePeriodRecord) => {
    Modal.confirm({
      title: 'Suspendre cet abonnement ?',
      content: `L'organisation "${r.organizationName || r.organizationId.slice(0, 8)}" sera suspendue immediatement.`,
      okText: 'Suspendre',
      okButtonProps: { danger: true },
      cancelText: 'Annuler',
      onOk: () =>
        new Promise<void>((resolve) => {
          suspend(
            {
              url: `/api/v1/admin/billing/subscriptions/${r.organizationId}/suspend`,
              method: 'post',
              values: {},
            },
            {
              onSuccess: () => {
                void message.success('Abonnement suspendu');
                void refetch();
                resolve();
              },
              onError: () => {
                void message.error('Erreur lors de la suspension');
                resolve();
              },
            },
          );
        }),
    });
  };

  if (records.length === 0 && !isLoading) return null;

  return (
    <div className="fc-card overflow-hidden p-0">
      <div className="flex items-center gap-2 border-b border-slate-200 px-5 py-4 dark:border-slate-700">
        <Clock className="h-4 w-4 text-amber-500" />
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
          Abonnements en grace period
        </h3>
        {records.length > 0 && (
          <Tag color="warning" className="ml-2">
            {records.length}
          </Tag>
        )}
      </div>
      <Table
        dataSource={records}
        loading={isLoading}
        rowKey="id"
        size="middle"
        scroll={{ x: 800 }}
        pagination={false}
      >
        <Table.Column
          dataIndex="organizationName"
          title="Organisation"
          render={(name: string, r: GracePeriodRecord) => (
            <span className="text-sm text-slate-900 dark:text-white">
              {name || r.organizationId?.slice(0, 8)}
            </span>
          )}
        />
        <Table.Column
          dataIndex="planType"
          title="Plan"
          width={100}
          render={(p: string) => <Tag>{p}</Tag>}
        />
        <Table.Column
          dataIndex="gracePeriodStart"
          title="Debut"
          width={110}
          render={(d: string) => (
            <span className="text-xs text-slate-500 dark:text-slate-400">{fmtDate(d)}</span>
          )}
        />
        <Table.Column
          dataIndex="gracePeriodEnd"
          title="Expiration"
          width={110}
          render={(d: string) => (
            <span className="text-xs text-slate-500 dark:text-slate-400">{fmtDate(d)}</span>
          )}
        />
        <Table.Column
          dataIndex="daysRemaining"
          title="Jours restants"
          width={120}
          render={(days: number) => (
            <Tag color={days <= 3 ? 'error' : days <= 7 ? 'warning' : 'default'}>
              {days} jour{days > 1 ? 's' : ''}
            </Tag>
          )}
        />
        <Table.Column
          title="Actions"
          width={180}
          render={(_: unknown, r: GracePeriodRecord) => (
            <div className="flex items-center gap-2">
              <Button
                size="small"
                type="primary"
                icon={<RefreshCw className="h-3 w-3" />}
                loading={reactivating}
                onClick={() => handleReactivate(r)}
              >
                Reactiver
              </Button>
              <Button
                size="small"
                danger
                icon={<Pause className="h-3 w-3" />}
                loading={suspending}
                onClick={() => handleSuspend(r)}
              >
                Suspendre
              </Button>
            </div>
          )}
        />
      </Table>
    </div>
  );
};

export default GracePeriodPanel;
